import { ExpiryTypes, MULTI_LEG_PENDING_TYPE, SpotType } from '@fnx/fx-pad';
import { ISO8601 } from '@alk/dates';
import { PartialWithExclutions } from '../types';
import { SetUpdateTransaction } from '.';

export type OrderReportType = 'spot' | 'forward' | 'swap';

export type IOrderMarketCloseness = 'close' | 'near' | 'far' | 'distant';

export const ORDER_TERMINAL_STATUSES =
    [
        'filled',
        'partiallyFilled',
        'partiallyCancelled',
        'cancelled',
        'rejected',
        'suspended',
    ] as const;

export type IOrderStatus = 'unknown' | 'executing' | 'active' | 'inactive' | typeof ORDER_TERMINAL_STATUSES[number];

export type PendingStrategy = 'singleOrder' | typeof MULTI_LEG_PENDING_TYPE[number];

export interface IOrderReport {
    orderType: OrderReportType;
    orderId: string;
    dealNumber?: string;
    state: IOrderStatus;
    fillPercent: number;
    spotType: SpotType;
    instrument: string;
    baseCurrency: string;
    side: string;
    traderId: string;
    traderName: string;
    clientId: number;
    clientName: string;
    userExtRefId?: string;
    clientExtRefId?: string;
    tradeDate: ISO8601;
    executionDate?: ISO8601;

    counterFilledAmount: number;
    counterCurrency: string;
    farCounterFilledAmount?: number;
    spotRate?: number;

    requestedAmount?: number;
    leftAmount?: number;
    filledAmount?: number;
    requestedPrice?: number;
    filledPrice?: number;
    valueDate?: ISO8601;

    nearAmount?: number;
    nearPrice?: number;
    nearPoints?: number;
    nearValueDate?: ISO8601;

    farAmount?: number;
    farPrice?: number;
    farPoints?: number;
    farValueDate?: ISO8601;

    expiryType?: ExpiryTypes;
    expiryDate?: ISO8601;

    marketPoints?: number;
    marketDistance?: number;
    marketCloseness?: IOrderMarketCloseness;

    pendingStrategyId?: string;
    pendingStrategy?: string;
    pendingLegType?: string;

    updateDate?: ISO8601;
}

export type ISharedOrderReport = Pick<IOrderReport,
'orderType' |
'orderId' |
'dealNumber' |
'state' |
'fillPercent' |
'spotType' |
'instrument' |
'baseCurrency' |
'traderId' |
'traderName'|
'side' |
'clientName' |
'clientId' |
'userExtRefId' |
'clientExtRefId' |
'tradeDate' |
'executionDate'
>;

export type ISpotOrderReport = ISharedOrderReport & Pick<IOrderReport,
'counterFilledAmount' |
'counterCurrency' |
'requestedAmount' |
'leftAmount' |
'filledAmount' |
'requestedPrice' |
'filledPrice' |
'valueDate'
>;

export type IForwardOrderReport = ISharedOrderReport & Pick<IOrderReport,
'nearAmount' |
'nearPrice' |
'nearPoints' |
'nearValueDate' |
'farAmount' |
'farPrice' |
'farPoints' |
'farValueDate' |
'farCounterFilledAmount' |
'counterFilledAmount' |
'counterCurrency' |
'spotRate'
>;

export type IPendingOrderReport = ISpotOrderReport & Pick<IOrderReport,
'marketCloseness' |
'marketDistance' |
'marketPoints' |
'expiryType' |
'pendingStrategyId' |
'pendingStrategy' |
'pendingLegType'
>;

export const ORDER_KEY = 'orderId';

export type IBlotterTransaction = SetUpdateTransaction<IOrderReport, typeof ORDER_KEY>;

export type IBlotterUpdate =
    Partial<IBlotterTransaction>
    & {
        flush: boolean,
        allOrders?: Map<IOrderReport[typeof ORDER_KEY], IOrderReport>
    };

export type IBlotterOrderUpdate = PartialWithExclutions<IOrderReport, typeof ORDER_KEY>;
